import type { CentralIconBaseProps } from '@central-icons-react/round-filled-radius-1-stroke-2/CentralIconBase';
import IconGroup from '@central-icons-react/round-filled-radius-1-stroke-2/IconGroup3';
import {
    InsightsOutlined as BiDashboardIcon,
    CardTravelOutlined as CardTravelOutlinedIcon,
    CasesOutlined as CasesOutlinedIcon,
    ContactsOutlined as ContactsOutlinedIcon,
    TuneOutlined as ControlRoomIcon,
    DescriptionOutlined as DescriptionOutlinedIcon,
    DomainOutlined as DomainOutlinedIcon,
    Shop2Outlined as Shop2OutlinedIcon,
    GroupsOutlined as TeamManagementIcon,
} from '@mui/icons-material';
import type SvgIcon from '@mui/material/SvgIcon';
import type { FunctionComponent } from 'react';
import Maybe from 'true-myth/maybe';

import type { FeatureFlagService } from '~/shared/feature-flag-service/feature-flag-service';
import type { Translate } from '~/shared/internationalization/translation';
import {
    IconAlignment,
    IconAnalyticsDashboard,
    IconControlRoom,
    IconDashboard,
    IconDocument,
    IconHumanInWindow,
    IconJobArchitecture,
    IconJobProfile,
    IconLearning,
    IconProfile,
    IconSettings,
    IconTalentDirectory,
    IconGroup as NewIconGroup,
} from '~/ui/core/icons';

import { learningOpportunitiesV2Route } from '../../learning-opportunities-v2';
import { route as dashboardPageRoute } from '../pages/dashboard/constants';
import { route as dashboardV2PageRoute } from '../pages/dashboard-v2/constants';
import { route as documentationPageRoute } from '../pages/documentation/constants';
import { route as jobsPageRoute } from '../pages/job-list/constants';
import { route as jobProfilesPageRoute } from '../pages/job-profiles/constants';
import { route as learningOpportunitiesPageRoute } from '../pages/learning-opportunities/constants';
import { route as myGrowthPageRoute } from '../pages/my-growth/constants';
import { route as profilePageRoute } from '../pages/profile/constants';
import { route as talentDirectoryPageRoute } from '../pages/talent-directory/constants';

type SvgIconComponent = FunctionComponent<CentralIconBaseProps> | typeof SvgIcon;

export type SideDrawerParentLinkProperties = {
    readonly children: readonly SideDrawerListItem[];
    readonly Icon: SvgIconComponent;
    readonly label: string;
};

export type SideDrawerLinkProperties = {
    readonly externalUrl: boolean;
    readonly Icon: SvgIconComponent;
    readonly label: string;
    readonly NewIcon: SvgIconComponent;
    readonly onRouteChange?: () => void;
    readonly path: string;
};

export type SideDrawerListItem = SideDrawerLinkProperties | SideDrawerParentLinkProperties;

type ListItemDependencies = {
    readonly onRouteChange?: () => void;
    readonly translate: Translate;
};

export type AnalyticsDashboardListItemDependencies = ListItemDependencies & {
    readonly canAccessBusinessIntelligence: boolean;
};

export const createAnalyticsDashboardListItem = (
    dependencies: AnalyticsDashboardListItemDependencies,
): Maybe<SideDrawerListItem> => {
    const { canAccessBusinessIntelligence, onRouteChange, translate } = dependencies;

    if (canAccessBusinessIntelligence) {
        return Maybe.just({
            externalUrl: false,
            Icon: BiDashboardIcon,
            label: translate('navigation.analytics-dashboard'),
            NewIcon: IconAnalyticsDashboard,
            onRouteChange,
            path: '/business-intelligence',
        });
    }

    return Maybe.nothing();
};
export type JobProfileManagementListItemDependencies = ListItemDependencies & {
    readonly canUserGetJobProfiles: boolean;
    readonly canUserManageJobs: boolean;
};

export const createJobProfileManagementListItem = (
    dependencies: JobProfileManagementListItemDependencies,
): Maybe<SideDrawerListItem> => {
    const { canUserGetJobProfiles, canUserManageJobs, onRouteChange, translate } = dependencies;

    if (canUserManageJobs || canUserGetJobProfiles) {
        const children: SideDrawerListItem[] = [];

        if (canUserManageJobs) {
            children.push({
                externalUrl: false,
                Icon: CardTravelOutlinedIcon,
                label: translate('navigation.job-postings'),
                NewIcon: IconHumanInWindow,
                onRouteChange,
                path: jobsPageRoute.patterns.jobs,
            });
        }

        if (canUserGetJobProfiles) {
            children.push({
                externalUrl: false,
                Icon: ContactsOutlinedIcon,
                label: translate('navigation.job-profiles'),
                NewIcon: IconJobProfile,
                onRouteChange,
                path: jobProfilesPageRoute.patterns.jobProfiles,
            });
        }

        return Maybe.just({
            children,
            Icon: CasesOutlinedIcon,
            label: translate('navigation.job-management'),
        });
    }

    return Maybe.nothing();
};

export type LearningManagementListItemDependencies = ListItemDependencies & {
    readonly canUserFilterLearningOpportunities: boolean;
    readonly featureFlagService: FeatureFlagService;
};

export const createLearningManagementListItem = (
    dependencies: LearningManagementListItemDependencies,
): Maybe<SideDrawerListItem> => {
    const { canUserFilterLearningOpportunities, featureFlagService, onRouteChange, translate } = dependencies;

    if (canUserFilterLearningOpportunities) {
        const path = featureFlagService.isFeatureFlagEnabled('new-learning-opportunities')
            ? learningOpportunitiesV2Route.patterns.learningOpportunities
            : learningOpportunitiesPageRoute.patterns.learningOpportunities;

        return Maybe.just({
            externalUrl: false,
            Icon: Shop2OutlinedIcon,
            label: translate('navigation.learning-management'),
            NewIcon: IconLearning,
            onRouteChange,
            path,
        });
    }

    return Maybe.nothing();
};

export type TalentDirectoryListItemDependencies = ListItemDependencies & {
    readonly canAccessTalentDirectory: boolean;
};

export const createTalentDirectoryListItem = (
    dependencies: TalentDirectoryListItemDependencies,
): Maybe<SideDrawerListItem> => {
    const { canAccessTalentDirectory, onRouteChange, translate } = dependencies;

    if (canAccessTalentDirectory) {
        return Maybe.just({
            externalUrl: false,
            Icon: DomainOutlinedIcon,
            label: translate('navigation.talent-directory'),
            NewIcon: IconTalentDirectory,
            onRouteChange,
            path: talentDirectoryPageRoute.patterns.talentDirectory,
        });
    }

    return Maybe.nothing();
};

export type FeatureFlagListItemDependencies = ListItemDependencies & {
    readonly featureFlagService: FeatureFlagService;
};

export const createPositionManagementItem = (
    dependencies: FeatureFlagListItemDependencies,
): Maybe<SideDrawerListItem> => {
    const { featureFlagService, onRouteChange, translate } = dependencies;

    if (featureFlagService.isFeatureFlagEnabled('position-management')) {
        return Maybe.just({
            externalUrl: false,
            Icon: TeamManagementIcon,
            label: translate('navigation.position-management'),
            NewIcon: NewIconGroup,
            onRouteChange,
            path: '/position-management',
        });
    }

    return Maybe.nothing();
};

export const createAlignmentRecordItem = (dependencies: FeatureFlagListItemDependencies): Maybe<SideDrawerListItem> => {
    const { featureFlagService, onRouteChange, translate } = dependencies;

    if (featureFlagService.isFeatureFlagEnabled('alignment-dialogue')) {
        return Maybe.just({
            externalUrl: false,
            Icon: TeamManagementIcon,
            label: translate('navigation.my-alignment'),
            NewIcon: IconAlignment,
            onRouteChange,
            path: '/my-alignment',
        });
    }

    return Maybe.nothing();
};

export type createControlRoomListItemDependencies = ListItemDependencies & {
    readonly canUseControlRoom: boolean;
    readonly featureFlagService: FeatureFlagService;
    readonly isAssumedTenant: boolean;
    readonly tenantId: Maybe<string>;
};

export const createJobArchitectureV2ListItem = (
    dependencies: FeatureFlagListItemDependencies,
): Maybe<SideDrawerListItem> => {
    const { featureFlagService, onRouteChange, translate } = dependencies;

    if (featureFlagService.isFeatureFlagEnabled('job-architecture-v-2')) {
        return Maybe.just({
            externalUrl: false,
            Icon: IconGroup,
            label: `${translate('navigation.job-architecture')}`,
            NewIcon: IconJobArchitecture,
            onRouteChange,
            path: '/job-architecture-v2',
        });
    }

    return Maybe.nothing();
};

export const createControlRoomListItem = (
    dependencies: createControlRoomListItemDependencies,
): Maybe<SideDrawerListItem> => {
    const { canUseControlRoom, featureFlagService, isAssumedTenant, onRouteChange, tenantId, translate } = dependencies;

    if (featureFlagService.isFeatureFlagEnabled('demo-menu')) {
        return Maybe.nothing();
    }

    if (canUseControlRoom && tenantId.isJust) {
        return Maybe.just({
            externalUrl: true,
            Icon: ControlRoomIcon,
            label: translate('navigation.control-room'),
            NewIcon: IconControlRoom,
            onRouteChange,
            path: `https://control-room.be.cobrainer.cloud/?tenantId=${tenantId.value}${isAssumedTenant ? '&assumeTenant=True' : ''}`,
        });
    }

    return Maybe.nothing();
};

export const createDashboardV2ListItem = (dependencies: FeatureFlagListItemDependencies): Maybe<SideDrawerListItem> => {
    const { featureFlagService, onRouteChange, translate } = dependencies;

    if (featureFlagService.isFeatureFlagEnabled('dashboard-v2')) {
        const isDemoMenuEnabled = featureFlagService.isFeatureFlagEnabled('demo-menu');
        const label = isDemoMenuEnabled ? translate('navigation.dashboard') : translate('navigation.dashboard-v2');

        return Maybe.just({
            externalUrl: false,
            Icon: BiDashboardIcon,
            label,
            NewIcon: IconAnalyticsDashboard,
            onRouteChange,
            path: dashboardV2PageRoute.patterns.dashboardV2,
        });
    }

    return Maybe.nothing();
};

export const createDashboardListItem = (
    dependencies: ListItemDependencies & { readonly featureFlagService: FeatureFlagService },
): Maybe<SideDrawerListItem> => {
    const { featureFlagService, onRouteChange, translate } = dependencies;

    if (featureFlagService.isFeatureFlagEnabled('demo-menu')) {
        return Maybe.nothing();
    }

    return Maybe.just({
        externalUrl: false,
        Icon: BiDashboardIcon,
        label: translate('navigation.dashboard'),
        NewIcon: IconDashboard,
        onRouteChange,
        path: dashboardPageRoute.patterns.dashboard,
    });
};

export const createDocumentationListItem = (dependencies: ListItemDependencies): Maybe<SideDrawerListItem> => {
    const { onRouteChange, translate } = dependencies;

    return Maybe.just({
        externalUrl: false,
        Icon: DescriptionOutlinedIcon,
        label: translate('navigation.documentation'),
        NewIcon: IconDocument,
        onRouteChange,
        path: documentationPageRoute.patterns.documentation,
    });
};

export const createProfileListItem = (dependencies: ListItemDependencies): Maybe<SideDrawerListItem> => {
    const { onRouteChange, translate } = dependencies;

    return Maybe.just({
        externalUrl: false,
        Icon: ContactsOutlinedIcon,
        label: translate('navigation.profile'),
        NewIcon: IconProfile,
        onRouteChange,
        path: profilePageRoute.patterns.usersMe,
    });
};

export const createMyGrowthListItem = (
    dependencies: ListItemDependencies & {
        readonly featureFlagService: FeatureFlagService;
        readonly showGrowthPage: boolean;
    },
): Maybe<SideDrawerListItem> => {
    const { featureFlagService, onRouteChange, showGrowthPage, translate } = dependencies;

    if (!showGrowthPage) {
        return Maybe.nothing();
    }

    if (featureFlagService.isFeatureFlagEnabled('demo-menu')) {
        return Maybe.nothing();
    }

    return Maybe.just({
        externalUrl: false,
        Icon: Shop2OutlinedIcon,
        label: translate('navigation.my-growth'),
        NewIcon: IconLearning,
        onRouteChange,
        path: myGrowthPageRoute.patterns.myGrowth,
    });
};

export const createSettingsListItem = (dependencies: ListItemDependencies): Maybe<SideDrawerListItem> => {
    const { onRouteChange, translate } = dependencies;

    return Maybe.just({
        externalUrl: false,
        Icon: Shop2OutlinedIcon,
        label: translate('common.settings'),
        NewIcon: IconSettings,
        onRouteChange,
        path: '/companySettings',
    });
};

export const createHome2ListItem = (
    dependencies: Pick<ListItemDependencies, 'onRouteChange'> & { readonly featureFlagService: FeatureFlagService },
): Maybe<SideDrawerListItem> => {
    const { featureFlagService, onRouteChange } = dependencies;

    if (!featureFlagService.isFeatureFlagEnabled('new-dashboard')) {
        return Maybe.nothing();
    }

    return Maybe.just({
        externalUrl: false,
        Icon: BiDashboardIcon,
        label: 'Home 2',
        NewIcon: IconDashboard,
        onRouteChange,
        path: '/home-2',
    });
};
